import React, { useEffect, useState, useCallback } from 'react';
import useForm from '@utils/useForm';
import { styled } from '@linaria/react';
import { css } from '@linaria/core';
import { Helpers } from '@utils/helpers';
import { GAAviva } from '@utils/gaAviva';
import { isEmpty, get, lowerCase } from 'lodash';
import { ITheme } from 'src/theme';
import { useAvivaSelector, useAvivaDispatch } from '@redux/hook';
import { useHistory } from 'react-router-dom';
import { HealthForm } from '@models/MLTC/Health.model';
import { updateHealth, completeStep } from '@redux/slices/MLTC/step3Slice';
import { postBMICheck, postSavingMyInfo } from '@api/index';
import { BMICheckRequest } from '@models/BMIRequest.model';

import { IMyInfoQuestionRequest } from '@models/MyInfoRequest.model';

import { LeftGrid, FullGrid, EmptyGrid, ContainerGrid, Container } from '@components/LayoutGrid';
import MultiSteps from '@components/MultiSteps';
import Heading from '@components/Heading';
import Input from '@components/Input';
import CheckboxList from '@components/CheckboxList';
import Checkbox from '@components/Checkbox';
import Text from '@components/Text';
import Button from '@components/Button';
import BulletList from '@components/BulletList';
import Bullet from '@components/Bullet';
import RadioButtonGroup from '@components/RadioButtonGroup';
import RadioButton from '@components/RadioButton';
import { validate } from './service';
import { setLoadingState } from '@redux/slices/loadingSlice';
import Link from '@components/Link';
import Segment from '@components/Segment';
import Modal from '@components/Modal';
import { UOB_CONTACT_URL } from '@static/config';

const page = 'About you';
export interface AboutYouProps {
  theme?: ITheme
}
const segment = css`
  padding: 32px 20px 30px;
  @media screen and (min-width: 768px) {
    padding: 32px 30px 30px;
  }
`;

const convertHealthQuestions = (values: HealthForm, httValue: any) => {
  let question =
    `22,${values['q1']}|` +
    `23,${values['q2']}|` +
    `24,${values['q3']}|` +
    `25,${values['q4']}|` +
    `26,${values['q5']}` +
    `${(values['q5'] === 'Y') ? `|27,${httValue}` : ''}`;
  return `${question}`;
}

const trackOnload = (): void => {
  const GA = GAAviva.getInstance();
  let { form } = GA.digitalData;
  form.name = 'Health Questions';
  GA.trackPageLoad({ form });
}

const trackFormSubmmit = (error: string = ''): void => {
  const GA = GAAviva.getInstance();
  if (isEmpty(error)) {
    GA.trackFormSuccess({});
    return;
  }
  const { form } = GA.digitalData;
  form.error.messages = error;
  GA.trackFormFailed({ form });
}

function AboutYou(props: AboutYouProps) {
  console.log(`QnB ➤ PAGE ➤ Aviva - ${page}`);

  const history = useHistory();
  const dispatch = useAvivaDispatch();


  const retrieveMyInfo = useAvivaSelector(state => state.mltcStep2.retrievedMyInfo);
  const personalDetails = useAvivaSelector(state => state.mltcStep2.personalDetails);
  const isStep2Completed = useAvivaSelector(state => state.mltcStep2.isCompleted);
  const basicDetails = useAvivaSelector(state => state.mltcStep1.basicDetails);
  const coverage = useAvivaSelector(state => state.mltcStep1.coverage);
  const initialHealth = useAvivaSelector(state => state.mltcStep3.health) || {};

  const [isPageLoaded, setIsPageLoaded] = useState<boolean>(false);
  const [httSelection, setHttSelection] = useState<string>('N');


  const { values, errors, handleChange, formValid, patchValue } = useForm(new HealthForm(initialHealth), () => { }, validate);

  const campaignDetail = useAvivaSelector(state => state.loading.campaignDetail);
  const paramCC = campaignDetail?.campaignCode ? `?cc=${campaignDetail.campaignCode}` : '';
  const combineParamCC = campaignDetail?.campaignCode ? `&cc=${campaignDetail.campaignCode}` : '';

  const [isUOB, setIsUOB] = useState<boolean>(false);
  const [isShowUOB, setIsShowUOB] = useState<boolean>(false);
  const gotoUOBContact = () => {
    window.open(UOB_CONTACT_URL, "_blank", "noopener noreferrer");
  };

  const gotoContact = useCallback(() => {
    if (isUOB) {
      setIsShowUOB(true);
    } else {
      history.push('/mltc-r2/contact' + paramCC);
    }
  }, [history, isUOB, setIsUOB]);

  const checkBmi = useCallback(async () => {
    const { weight, height } = values;
    const dob = personalDetails?.dob;
    const age = Helpers.getAge(new Date(dob || ''));
    try {
      const payload = new BMICheckRequest({ weight, height, age, prodcode: coverage?.plan });
      const response = await postBMICheck(payload);
      const status = get(response, 'data.status', '');
      const bmi = Number(get(response, 'data.bmi', ''));
      console.debug(`QnB ➤ checkBmi() ➤ `, { request: payload, response, status, bmi, result: (lowerCase(status) !== 'decline') && (bmi >= 17 && bmi <= 29) });
      // return true;
      return (lowerCase(status) !== 'decline') // && (bmi >= 17 && bmi <= 29);
    } catch (error) {
      console.error(error);
      return false;
    }
  }, [values, personalDetails, coverage]);

  const updateMyInfoAndContinue = useCallback(async () => {
    const id = retrieveMyInfo?.id || 0;
    const healthQuestions = convertHealthQuestions(values, httSelection);

    const height = Number(values['height']);
    const weight = Number(values['weight']);

    const payload: IMyInfoQuestionRequest = {
      id,
      healthQuestions,
      height,
      weight,
      smokingStatus: basicDetails?.smoker === 'Y' ? 'Yes' : 'No'
    };

    try {
      const response = await postSavingMyInfo(payload);
      console.debug(`QnB ➤ postSavingMyInfo(payload) ➤ `, { payload, response, q5: values['q5'], httSelection });
      dispatch(completeStep(true));

      if ((values['q5'] === 'Y') && httSelection == 'Y') {
        gotoContact();
      } else {
        const params = new URLSearchParams(window.location.href.split('?')[1]);
        const inject = params.get('inject');
        history.push('/mltc-r2/declaration' + paramCC + (inject ? `?inject=true` : ''));
      }
    } catch (error) {
      console.debug(`QnB ➤ postSavingMyInfo(payload) ➤ error  ➤ `, { error });
      dispatch(completeStep(true));
      history.push('/mltc-r2/declaration' + paramCC); //bypass first
    }
  }, [retrieveMyInfo, history, dispatch, values, basicDetails]);

  const needToGoToContact = useCallback(() => {
    const basedDeclinedAnswer =
      values['q1'] === 'Y'
      || values['q2'] === 'Y'
      || values['q3'] === 'Y'
      || values['q4'] === 'Y'
      || (values['q5'] === 'Y' && httSelection === 'Y');

    if (basedDeclinedAnswer) {
      return true;
    }
    return false;
  }, [values, httSelection]);

  const onClickContinue = useCallback(async () => {
    dispatch(setLoadingState(true));
    dispatch(updateHealth({ ...values, isSmoker: basicDetails?.smoker }));
    if (!await checkBmi() || needToGoToContact()) {
      gotoContact();
      dispatch(setLoadingState(false));
      return;
    }
    trackFormSubmmit();
    await updateMyInfoAndContinue();
    dispatch(setLoadingState(false));
  }, [values, gotoContact, dispatch, needToGoToContact, checkBmi]);



  const onBack = () => {
    history.push('/mltc-r2/my-info?step=2' + combineParamCC)
  }

  const onChangeHTT = (value: any) => {
    console.log(`QnB ➤ onChangeHTT(value)`, { value });
    setHttSelection(value);
    patchValue({ q6: value })
    console.log(`QnB ➤ onChangeHTT(value) ➤ end of function`, { httSelection });
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.href.split('?')[1]);
    const inject = params.get('inject');
    console.log(
      'QnB ➤ useEffect() ➤',
      {
        'step1Slice ➤ coverage': coverage,
        'step1Slice ➤ basicDetails': basicDetails,
        'step3Slice ➤ health': initialHealth,
        'local ➤ values': values,
        'local ➤ inject': inject,
        campaignDetail
      }
    );
    if (!isPageLoaded) {
      trackOnload();
      if (campaignDetail && campaignDetail?.code == 'LUB1') {
        setIsUOB(true);
      }

      if (values['q6']) setHttSelection(values['q6']);
      if (!isStep2Completed && process.env.REACT_APP_INJECT_CHECK !== 'true') {
        dispatch(completeStep(false));
        history.push('/mltc-r2/my-info' + paramCC);
      } else {
        setIsPageLoaded(true);
      }
      setIsPageLoaded(true);

      if (inject === 'true') {
        patchValue({
          height: '171',
          weight: '81',
          q1: 'N',
          q2: 'N',
          q3: 'N',
          q4: 'N',
          q5: 'N',
        })
      }
    }
  }, [isPageLoaded, setIsPageLoaded, setIsUOB, history, isStep2Completed, basicDetails]);


  return (
    <Container>
      <ContainerGrid>
        <FullGrid>
          <MultiSteps steps={5} step={3} label={`${'Health questions'}`} />
          <Heading margin="0 0 30px"> More about yourself </Heading>
        </FullGrid>
        <LeftGrid>

          <React.Fragment>
            <Heading variant="label" margin="0 0 15px">Height</Heading>
            {!errors?.height?.isValid && errors?.height?.message && (<Bullet variant="error">{errors?.height?.message}</Bullet>)}
            <Input variant="suffix" boxLabel="cm" maxLength={3} type="number" invalid={!isEmpty(errors?.height?.message)} value={values['height']} name="height" onChange={handleChange} ></Input>

            <Heading variant="label" margin="0 0 15px">Weight</Heading>
            {!errors?.weight?.isValid && errors?.weight?.message && (<Bullet variant="error">{errors?.weight?.message}</Bullet>)}
            <Input variant="suffix" boxLabel="kg" invalid={!isEmpty(errors?.weight?.message)} maxLength={3} type="number" value={values['weight']} name="weight" onChange={handleChange}></Input>

            <Heading variant="label" margin="0 0 10px"> Do you suffer from, or have you been treated for the following conditions?</Heading>
            <BulletList variant="default" bottomMargin="small" margin="0 0 15px">
              <Bullet>Cancer, diabetes, stroke, AIDS or HIV infection</Bullet>
              <Bullet>Heart disease (cardiomyopathy, coronary artery disease, heart attack, ischaemic heart disease)</Bullet>
              <Bullet>Kidney disease (chronic kidney disease, kidney failure, kidney or ureter tumour)</Bullet>
              <Bullet>Liver disease (cirrhosis, hepatitis C, autoimmune hepatitis, liver failure, alcoholic liver disease, liver cancer, hepatoma)</Bullet>
              <Bullet>Lung disease (lung tumour, sarcoidosis, bronchiectasis or pneumoconiosis, chronic obstructive pulmonary disease)</Bullet>
              <Bullet>Dementia, Alzheimer&apos;s disease, Parkinson&apos;s disease, multiple sclerosis, motor neurone disease</Bullet>
            </BulletList>
            <RadioButtonGroup value={values['q1']} name="q1" onChange={handleChange} >
              <RadioButton text="Yes" value="Y" />
              <RadioButton text="No" value="N" />
            </RadioButtonGroup>

            <Heading variant="label" margin="0 0 10px"> Do you suffer from, or have you been treated for the following conditions? </Heading>
            <BulletList variant="default" bottomMargin="small" margin="0 0 15px">
              <Bullet>Arthritis, hypertension or high cholesterol</Bullet>
              <Bullet>Any condition(s) for which you are on medication that lasted more than 14 days, require medical treatment or follow up, or have recurring symptoms and have yet to seek medical advice</Bullet>
            </BulletList>
            <RadioButtonGroup value={values['q2']} name="q2" onChange={handleChange} >
              <RadioButton text="Yes" value="Y" />
              <RadioButton text="No" value="N" />
            </RadioButtonGroup>

            <Heading variant="label" margin="0 0 15px"> Do you need the assistance of another person or mechanical aids to perform activities of daily living such as washing, dressing, feeding, toileting, walking or moving around and transferring? </Heading>
            <RadioButtonGroup value={values['q3']} name="q3" onChange={handleChange} >
              <RadioButton text="Yes" value="Y" />
              <RadioButton text="No" value="N" />
            </RadioButtonGroup>

            <Heading variant="label" margin="0 0 10px">Have you, in the last 12 months, experienced any reduced or restricted ability in doing any of the following day-to-day activities due to your health condition(s)?</Heading>
            <BulletList variant="default" bottomMargin="small" margin="0 0 15px">
              <Bullet>Housework, preparing meals, shopping for groceries or personal needs</Bullet>
              <Bullet>Using public transport or participating in social activities</Bullet>
            </BulletList>
            <RadioButtonGroup value={values['q4']} name="q4" onChange={handleChange} >
              <RadioButton text="Yes" value="Y" />
              <RadioButton text="No" value="N" />
            </RadioButtonGroup>

            <Heading variant="label" margin="0 0 10px">Does your total monthly benefit for Long Term Care Coverage exceed $3,000 (current application plus existing cover with us and other insurers)?</Heading>
            <Text margin="0 0 15px">For information on Infographic &quot;Moratorium on Genetic Testing and Insurance&quot;, please  <Link text="click here" target="_blank" rel="noopener noreferrer" url='https://singlife.com/content/dam/public/sg/documents/consumer-guides/genetics-moratorium.pdf'></Link></Text>
            <RadioButtonGroup value={values['q5']} name="q5" onChange={handleChange}>
              <RadioButton text="Yes" value="Y" />
              <RadioButton text="No" value="N" />
            </RadioButtonGroup>
            {values['q5'] === "N" && (
              <Segment hasBorderTop className={segment}>
                <Text>You do not need to tell us about your predictive genetic test results, unless it is negative and may help your application. Please contact us <Link text="here" onClick={gotoContact} variant='button'></Link></Text>
              </Segment>
            )}
            {values['q5'] === "Y" && (
              <Segment hasBorderTop className={segment}>
                <Heading variant="label" margin="0 0 12px">Have you done predictive genetic test on Huntington&apos;s disease (HTT)?</Heading>
                <CheckboxList margin={0}>
                  <Checkbox checked={httSelection == 'N'} variant="border" type="radio" onChange={() => onChangeHTT('N')}>
                    <Text>Not tested before</Text>
                  </Checkbox>
                  <Checkbox checked={httSelection == 'Y'} variant="border" type="radio" onChange={() => onChangeHTT('Y')} margin={0}>
                    <Text>Test done</Text>
                  </Checkbox>
                </CheckboxList>

              </Segment>
            )}

            <Button label="Continue" onClick={onClickContinue} disabled={!formValid} margin="40px 0 20px" />
          </React.Fragment>
          <Button onClick={onBack} variant="secondary" label="Back" margin="0 0 40px" />
        </LeftGrid>
        <EmptyGrid></EmptyGrid>
        {/* <RightGrid>
          <ContactBox items={contactInformation} />
        </RightGrid> */}

        <Modal show={isShowUOB} onClose={() => { setIsShowUOB(false) }}>
          <Text margin={'0px 0 30px'}>You will be redirected to our appointed intermediary - United Overseas Bank Limited (“UOB”) site to submit the contact me form.</Text>
          <Button margin="0 0 20px" label="Proceed" onClick={gotoUOBContact} />
          <Button variant="secondary" label="Cancel" margin={0} onClick={() => { setIsShowUOB(false) }} />
        </Modal>

      </ContainerGrid>
    </Container>
  );
};

export default AboutYou;